/**
 * Extracts the filename from a `Content-Disposition` response header.
 *
 * Handles the common server-emitted shapes for the Phase 6 export endpoints
 * (see D12 — backend always emits ASCII filenames, so RFC 5987 `filename*=`
 * support is intentionally not required):
 *
 *   - `attachment; filename="x.ics"`                       → `"x.ics"`
 *   - `attachment; filename=x.ics`                         → `"x.ics"`
 *   - `attachment; filename="x.ics"; size=123`             → `"x.ics"`
 *   - `inline` (no filename param)                         → `null`
 *   - `null` input (header missing on the response)        → `null`
 *
 * Returns `null` whenever no usable filename was found; callers fall back to a
 * type-specific default in that case so the download still saves with a
 * sensible name.
 */
export function parseContentDispositionFilename(
  header: string | null,
): string | null {
  if (header === null) return null;
  // Match `filename=` then either a quoted-string or an unquoted token up to
  // the next semicolon. The non-greedy quoted match keeps trailing `; size=…`
  // params from being swallowed into the captured filename.
  const match = /filename\s*=\s*(?:"([^"]*)"|([^;]+))/i.exec(header);
  if (!match) return null;
  const raw = match[1] ?? match[2] ?? "";
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}
